import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { findByPermalink, galleryItemsQueryOptions } from "@/lib/gallery.queries";
import { getNewsArticleByLegacyId } from "@/lib/news.functions";
import { newsArticleQueryOptions } from "@/lib/news.queries";
import {
  legacyArticleSlugCandidates,
  parseLegacyArticleId,
  resolveLegacyPath,
  withPreservedParams,
} from "@/lib/legacy-redirects";

/**
 * Catch-all for paths no other route owns. Its only job is the legacy-site
 * redirect layer (see lib/legacy-redirects.ts): old miinews.com links land on
 * the equivalent new page with a `replace` navigation instead of the 404
 * shell. Anything unresolved falls through to the root notFoundComponent.
 *
 * Order: static tables (sync) -> /Article?id=N via the API (articles imported
 * after the table was generated) -> /<permalink> via the article API -> 404.
 * The router is case-insensitive, so /Article and /article both arrive here.
 */
export const Route = createFileRoute("/$")({
  beforeLoad: async ({ location, context }) => {
    const { pathname, searchStr, hash } = location;
    const go = (href: string): never => {
      throw redirect({ href, replace: true });
    };

    const target = resolveLegacyPath(pathname, searchStr, hash);
    if (target) go(await verifyBeautyShowTarget(context.queryClient, target));

    const legacyId = parseLegacyArticleId(pathname, searchStr);
    if (legacyId !== null) {
      const slug = await lookupSlugByLegacyId(legacyId);
      go(withPreservedParams(slug ? `/news/${slug}` : "/news", searchStr));
    }

    for (const candidate of legacyArticleSlugCandidates(pathname)) {
      if (await articleExists(context.queryClient, candidate)) {
        go(withPreservedParams(`/news/${candidate}`, searchStr));
      }
    }

    throw notFound();
  },
  // Show the fallback immediately while an article lookup runs (the default
  // waits 1s before swapping the pending component in).
  pendingMs: 0,
  pendingComponent: Redirecting,
  component: Redirecting,
});

function Redirecting() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-24 text-center text-sm text-muted-foreground">
      Redirecting…
    </div>
  );
}

async function lookupSlugByLegacyId(id: number): Promise<string | null> {
  try {
    const row = await getNewsArticleByLegacyId({ data: { id } });
    return row?.slug ?? null;
  } catch {
    // Unknown action (API not yet deployed) or a transient failure: the
    // news list is still the right place to land.
    return null;
  }
}

async function articleExists(queryClient: QueryClient, slug: string): Promise<boolean> {
  try {
    const article = await queryClient.ensureQueryData(newsArticleQueryOptions(slug));
    return !!article?.body_html;
  } catch {
    return false;
  }
}

/**
 * Legacy /beauty/<videoId> maps to /beauty-show/<permalink>, but the old
 * route ignored its path param, so the id may not be a real permalink. Land
 * on the collection page rather than the episode's not-found screen. The
 * list is what the destination loads anyway, so this costs nothing extra.
 */
async function verifyBeautyShowTarget(queryClient: QueryClient, target: string): Promise<string> {
  const match = target.match(/^\/beauty-show\/([^?#]+)(.*)$/);
  if (!match) return target;
  const [, permalink, rest] = match;
  try {
    const items = await queryClient.ensureQueryData(galleryItemsQueryOptions("beauty-episode"));
    if (findByPermalink(items, decodeURIComponent(permalink))) return target;
  } catch {
    // fall through to the collection page
  }
  return `/beauty-show${rest}`;
}
